import { getWishlistHandles } from "../elements/wishlist-toggle.js";

/** Decorates the existing Webflow desktop/mobile wishlist icon links. */
export function attachWishlistBadge(): void {
  if (document.getElementById("lmdr-wishlist-badge-style")) return;
  const style = document.createElement("style");
  style.id = "lmdr-wishlist-badge-style";
  style.textContent = `
    .lmdr-wishlist-link.is-populated .lmdr-wishlist-original {
      color:var(--lmdr-wishlist-heart-color,#a10e1b); }
    .lmdr-wishlist-original { transform-origin:center; transform-box:view-box; }
    .lmdr-wishlist-badge { fill:currentColor; font-family:Arial,sans-serif;
      font-weight:600; text-anchor:middle; dominant-baseline:central; pointer-events:none; }
    .lmdr-wishlist-badge[hidden] { display:none!important; }
    .lmdr-wishlist-status { position:absolute; width:1px; height:1px; padding:0;
      margin:-1px; overflow:hidden; clip-path:inset(50%); white-space:nowrap; border:0; }
  `;
  document.head.append(style);
  const status = document.createElement("span");
  status.className = "lmdr-wishlist-status";
  status.setAttribute("role", "status");
  status.setAttribute("aria-live", "polite");
  status.setAttribute("aria-atomic", "true");
  document.body.append(status);
  const animations = new WeakMap<SVGSVGElement, Animation>();
  const update = (added = false, announce = false): void => {
    const count = getWishlistHandles().length;
    const label = `Liste de souhaits : ${count} article${count === 1 ? "" : "s"}`;
    document.querySelectorAll<HTMLAnchorElement>('a[href], [data-lmdr-wishlist-link]').forEach(link => {
      // Explicit hook for future Webflow navs; current icon links use link-block-42.
      if (!link.hasAttribute("data-lmdr-wishlist-link")) {
        if (!link.classList.contains("link-block-42")) return;
        const url = new URL(link.href, location.href);
        if (url.origin !== location.origin || !/\/wishlist\/?$/.test(url.pathname)) return;
      }
      link.classList.add("lmdr-wishlist-link");
      link.classList.toggle("is-populated", count > 0);
      // Keep Webflow's exact SVG, path, viewBox and responsive dimensions.
      const icon = link.querySelector<SVGSVGElement>("svg");
      if (!icon) return;
      icon.classList.add("lmdr-wishlist-original");
      let badge = icon.querySelector<SVGTextElement>(".lmdr-wishlist-badge");
      if (!badge) {
        badge = document.createElementNS("http://www.w3.org/2000/svg", "text");
        badge.classList.add("lmdr-wishlist-badge");
        badge.setAttribute("aria-hidden", "true");
        icon.append(badge);
      }
      const value = String(count);
      const box = icon.viewBox.baseVal;
      const width = box.width || 256;
      const height = box.height || 256;
      badge.setAttribute("x", String(box.x + width / 2));
      badge.setAttribute("y", String(box.y + height * .52));
      badge.setAttribute("font-size", String(Math.min(width * .3, width * .65 / (value.length * .65))));
      if (badge.textContent !== value) badge.textContent = value;
      badge.toggleAttribute("hidden", count === 0);
      link.setAttribute("aria-label", label);
      if (added && count && !matchMedia("(prefers-reduced-motion: reduce)").matches && icon.animate) {
        animations.get(icon)?.cancel();
        animations.set(icon, icon.animate([
          { transform: "scale(1)" },
          { transform: "scale(1.12)", offset: .25 },
          { transform: "scale(1)", offset: .5 },
          { transform: "scale(1.06)", offset: .72 },
          { transform: "scale(1)" },
        ], { duration: 520, easing: "ease-in-out" }));
      }
    });
    if (announce) status.textContent = `${added ? "Ajouté à" : "Retiré de"} la liste de souhaits. ${count} article${count === 1 ? "" : "s"}.`;
  };
  update();
  document.addEventListener("lmdr:wishlist_updated", event => {
    const detail = (event as CustomEvent<{ added?: boolean }>).detail;
    update(detail?.added === true, typeof detail?.added === "boolean");
  });
  // Webflow can insert another navigation after initial boot.
  const observer = new MutationObserver(records => {
    if (records.some(record => Array.from(record.addedNodes).some(node =>
      node instanceof Element && (node.matches("a, [data-lmdr-wishlist-link]") || node.querySelector("a, [data-lmdr-wishlist-link]")),
    ))) update();
  });
  observer.observe(document.body, { childList: true, subtree: true });
}
